/**
 * Non-Redux token store — the transport read source for the primary-session JWT
 * bearer (auth-redux-elimination, slice-2 keystone).
 *
 * It holds the five auth-token fields that today live on `state.auth`. The
 * transport request-config boundary (`axios.selectors`, consumed by the
 * request-config helper) sources the primary-session Authorization bearer from
 * here; on the browser Redux only MIRRORS into it (see `tokenStore.interop.ts`).
 *
 * ## Request scoping (browser vs. server)
 *
 * On the BROWSER there is exactly one user per JS runtime, so the tokens live in a
 * module singleton — correct and idiomatic.
 *
 * On the SERVER a single Node process multiplexes EVERY concurrent SSR request, so
 * a module singleton cannot represent per-request auth: one request could overwrite
 * the global bearer while another's render is still reading it, leaking one user's
 * Authorization into another's response. To keep the SAME read API isomorphic, all
 * reads and writes route through a pluggable *context resolver*. The server installs
 * an AsyncLocalStorage-backed resolver (see `tokenStore.server.ts`) that returns the
 * current request's token context; the browser installs nothing, so reads fall
 * through to the module singleton. The resolver is the ONLY thing that differs
 * between runtimes — this module stays browser-safe (no `async_hooks` import).
 *
 * Hydration: this store is NOT hydrated from cookies at module-load time. On the
 * browser the interop mirror (Redux -> store) fires on every auth-token change,
 * including the SSR-seeded initial state. On the server the per-request context is
 * seeded directly from the request cookies before the first authed request is built
 * (`runWithTokenContext` in `src/server.ts`). See the `auth-redux-migration`
 * skill reference for the SSR request-scoping STOP condition.
 *
 * Reactivity: this store is intentionally NON-reactive (slice-2 reactivity option
 * A). App.tsx keeps reading the mirrored Redux `useSelector` values to drive its
 * effects; only the transport read source moves here. Do NOT place a bare
 * `tokenStore.getX()` in a React dependency array — it will silently break the
 * cookie-sync / renew / heartbeat effects.
 */

export type AuthTokens = {
  jwt: string | undefined
  refreshToken: string | undefined
  cspJwt: string | undefined
  cspRefreshToken: string | undefined
  userSessionTokenId: string | undefined
}

const emptyTokens = (): AuthTokens => ({
  jwt: undefined,
  refreshToken: undefined,
  cspJwt: undefined,
  cspRefreshToken: undefined,
  userSessionTokenId: undefined,
})

/**
 * A mutable holder for one logical session's tokens. The browser uses exactly one
 * (the module singleton below); the server creates one per request inside its
 * AsyncLocalStorage context.
 */
export type TokenContext = { tokens: AuthTokens }

/**
 * Browser / default context. On the server this is never written — every request
 * resolves to its own context — so a stray uncontextualized server read fails closed
 * to "no bearer" rather than leaking another request's token.
 */
const moduleContext: TokenContext = { tokens: emptyTokens() }

/**
 * Resolver indirection. The default returns `undefined` (browser, or no request
 * context active), so reads fall through to `moduleContext`. The server overrides
 * this once at boot via `setTokenContextResolver`.
 */
let resolveContext: () => TokenContext | undefined = () => undefined

/**
 * Install a request-scoped token-context resolver. The server calls this once at
 * boot with an AsyncLocalStorage-backed resolver so concurrent requests each
 * read/write their own tokens. The browser never calls it.
 */
export const setTokenContextResolver = (
  resolver: () => TokenContext | undefined,
): void => {
  resolveContext = resolver
}

/**
 * The active context for the current execution: the per-request context on the
 * server, the module singleton on the browser (or whenever no request context is
 * active).
 */
const current = (): TokenContext => resolveContext() ?? moduleContext

export const tokenStore = {
  /** Primary-session JWT bearer (today `state.auth.jwt`). */
  getAccessToken: (): string | undefined => current().tokens.jwt,
  getRefreshToken: (): string | undefined => current().tokens.refreshToken,
  getCspJwt: (): string | undefined => current().tokens.cspJwt,
  getCspRefreshToken: (): string | undefined =>
    current().tokens.cspRefreshToken,
  getUserSessionTokenId: (): string | undefined =>
    current().tokens.userSessionTokenId,

  /**
   * Replace the full token snapshot for the active context. On the browser the
   * interop mirror calls this with the current `state.auth` token fields on every
   * change, so the store always equals Redux. Full-snapshot (not per-field)
   * semantics keep mirror writes total: a field that becomes `undefined` in Redux
   * is cleared here too.
   */
  setTokens: (next: AuthTokens): void => {
    current().tokens = {
      jwt: next.jwt,
      refreshToken: next.refreshToken,
      cspJwt: next.cspJwt,
      cspRefreshToken: next.cspRefreshToken,
      userSessionTokenId: next.userSessionTokenId,
    }
  },

  /** Reset every field to `undefined` for the active context. */
  clear: (): void => {
    current().tokens = emptyTokens()
  },
}
